// frontend/components/Layout/AdminLayout.jsx
import React, { useState } from 'react';
import { Outlet, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import {
  HomeIcon,
  ShoppingCartIcon,
  DocumentTextIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  UsersIcon,
  CubeIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  BuildingOfficeIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';

// ✅ Updated base navigation - Dashboard href changed to '/dashboard'
const baseNavigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },  // Changed from '/' to '/dashboard'
  { name: 'POS', href: '/pos', icon: ShoppingCartIcon },
  { name: 'Invoices', href: '/invoices', icon: DocumentTextIcon },
  { name: 'Reports', href: '/reports', icon: ChartBarIcon },
  { name: 'Expenses', href: '/expenses', icon: CurrencyDollarIcon },
  { name: 'Customers', href: '/customers', icon: UsersIcon },
  { name: 'Products', href: '/products', icon: CubeIcon },
  { name: 'SMS Composer', href: '/sms-composer', icon: EnvelopeIcon },
  { name: 'Settings', href: '/settings', icon: Cog6ToothIcon },
];

// Super Admin additional navigation
const superAdminNavigation = [
  { name: 'Super Admin', href: '/super-admin', icon: BuildingOfficeIcon },
];

// Get navigation based on user role
const getNavigation = (role) => {
  if (role === 'superadmin') {
    return [...superAdminNavigation, ...baseNavigation];
  }
  return baseNavigation;
};

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Get role-based navigation
  const navigation = getNavigation(user?.role);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-40 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="relative flex w-full max-w-xs flex-1 flex-col bg-white">
          <div className="absolute right-0 top-0 -mr-12 pt-4">
            <button onClick={() => setSidebarOpen(false)} className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white">
              <XMarkIcon className="h-6 w-6 text-white" />
            </button>
          </div>
          <SidebarContent user={user} navigation={navigation} handleLogout={handleLogout} />
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <SidebarContent user={user} navigation={navigation} handleLogout={handleLogout} />
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        <div className="sticky top-0 z-10 bg-white shadow-sm lg:hidden">
          <div className="flex h-16 items-center justify-between px-4">
            <button onClick={() => setSidebarOpen(true)}><Bars3Icon className="h-6 w-6" /></button>
            <div className="text-lg font-semibold">Brominon Admin</div>
            <div className="w-6" />
          </div>
        </div>
        <main className="py-6 px-4 sm:px-6 lg:px-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function SidebarContent({ user, navigation, handleLogout }) {
  return (
    <div className="flex flex-1 flex-col overflow-y-auto pt-5 pb-4">
      <div className="flex flex-shrink-0 items-center px-4">
        <BuildingOfficeIcon className="h-8 w-8 text-blue-600 mr-2" />
        <h1 className="text-2xl font-bold text-blue-600">Brominon</h1>
      </div>
      <div className="mt-5 flex flex-1 flex-col">
        <nav className="flex-1 space-y-1 px-2">
          {navigation.map((item) => (
            <Link 
              key={item.name} 
              to={item.href} 
              className="group flex items-center px-2 py-2 text-sm font-medium rounded-md text-gray-600 hover:bg-gray-50 hover:text-gray-900"
            >
              <item.icon className="mr-3 h-5 w-5" />
              {item.name}
            </Link>
          ))}
        </nav>
      </div>
      <div className="border-t border-gray-200 pt-4 pb-3">
        <div className="px-2">
          <div className="text-sm font-medium text-gray-500">
            {user?.role === 'superadmin' ? 'Super Admin' : 'Admin'}
          </div>
          <div className="text-sm font-semibold text-gray-900">{user?.full_name}</div>
          <div className="text-xs text-gray-500 capitalize">{user?.role}</div>
        </div>
        <div className="mt-3 px-2">
          <button onClick={handleLogout} className="group flex w-full items-center px-2 py-2 text-sm font-medium rounded-md text-red-600 hover:bg-red-50">
            <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5" />
            Logout
          </button>
        </div>
      </div>
    </div>
  );
}